/**
 * ARRASTAR PARA REORDENAR, porte de `ativarArrasteAbas` (`admin.html:9105-9154`; peça transversal
 * 17 do inventário), usado em "Vínculo Produto Aba" (uma lista por produto) e em "Roteiro Cliente".
 *
 * SÃO DOIS CAMINHOS, como no legado, porque o arraste do HTML5 não dispara em tela de toque:
 *   1. MOUSE: `draggable`, `dragstart`, `dragover` e `dragend`. A linha muda de lugar AO VIVO,
 *      assim que o ponteiro passa da metade da vizinha, e o `dragend` grava a ordem.
 *   2. TOQUE: Pointer Events com `pointerType === 'touch'`. Não começa em botão nem em link,
 *      captura o ponteiro, acha a linha embaixo do dedo por `elementFromPoint` e só troca com linha
 *      do MESMO grupo; o `pointerup` grava a ordem.
 *
 * O QUE FICA IGUAL MESMO PARECENDO DEFEITO, porque migração reproduz: o `dragend` e o `pointerup`
 * gravam a ordem mesmo quando nada mudou de lugar (um toque na linha sem mexer grava a mesma ordem),
 * e o `pointercancel` não é ouvido (se o navegador tomar o gesto para rolar, a linha fica apagada
 * até o próximo toque), exatamente como no legado.
 *
 * COMO A ORDEM MORA: o legado move o nó do DOM (`insertBefore`); aqui a ordem ao vivo é estado, e o
 * React move o nó. A ordem volta à dos dados quando os dados mudam (gravou, recarregou, removeu), e
 * fica como está quando a gravação falha, que é o que o DOM do legado faz.
 *
 * ACHADO DA FRENTE A6, MEDIDO em 19/09/2026 (defeito do legado, REPRODUZIDO por decisão do fundador):
 * no Chrome com toque, o arraste por toque do legado NÃO reordena. O primeiro `insertBefore` da
 * linha arrastada, mesmo quando ela não muda de lugar, a tira e a recoloca no DOM, e isso SOLTA a
 * captura do ponteiro (`lostpointercapture` já no segundo `pointermove`). Dali em diante o dedo cai
 * nas outras linhas, cujo `arrastandoTouch` (um por linha, no closure de cada uma) é falso: nada
 * mais se move, o `pointerup` não grava, e a linha fica apagada. Medido com a função copiada literal
 * de `admin.html:9105-9154`, descendo e subindo, com o resultado
 * `{"ordem":"a,b,d","terminou":0,"perdas":1}`, e guardado na suíte (`testes/a6.spec.ts`, caso
 * "o toque do LEGADO"). O React só move o nó quando a ordem muda, então sozinho ele NÃO reproduz
 * isso: a reprodução mora num ponto só, a constante `REPRODUZIR_PERDA_DE_CAPTURA_NO_TOQUE`, e o
 * estado do toque é por linha, como o do legado. Não medido: Safari (WebKit não está instalado aqui).
 */

// Estado, referência, memória e efeito do React, e os tipos dos eventos.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type PointerEvent as EventoDePonteiro,
} from 'react';

// O atributo que marca uma linha arrastável e guarda a chave dela, para o toque achar o alvo.
const ATRIBUTO = 'data-chave-arraste';

/**
 * O PONTO ÚNICO do defeito do toque (achado no cabeçalho). `true` reproduz o legado: a cada linha
 * que o dedo encontra, a captura é solta, como o `insertBefore` do legado a solta. `false` NÃO
 * conserta sozinho: quando o React move o próprio nó arrastado, o Chrome solta a captura do mesmo
 * jeito, e o conserto pediria recapturar o dedo. Isso é melhoria, e melhoria é decisão do fundador.
 */
const REPRODUZIR_PERDA_DE_CAPTURA_NO_TOQUE = true;

/** Move `chave` para antes (ou depois) de `alvo` na lista, como o `insertBefore` do legado. */
function mover(ordem: readonly string[], chave: string, alvo: string, depois: boolean): string[] {
  // Tira a linha arrastada do lugar dela.
  const sem = ordem.filter((c) => c !== chave);
  // O índice do alvo na lista já sem a arrastada.
  const indice = sem.indexOf(alvo);
  // Alvo que sumiu no meio do gesto: nada muda.
  if (indice === -1) return [...ordem];
  // Depois da metade da vizinha, entra depois dela; antes, entra antes (`nextSibling` ou a própria).
  sem.splice(depois ? indice + 1 : indice, 0, chave);
  return sem;
}

/** Se o ponto está na metade de baixo do elemento: a mesma conta do legado (`admin.html:9122`). */
function passouDaMetade(elemento: Element, clientY: number): boolean {
  const caixa = elemento.getBoundingClientRect();
  return clientY - caixa.top > caixa.height / 2;
}

export interface ArrasteDeLinhas {
  // A ordem ao vivo das chaves, que a tela usa para desenhar as linhas.
  readonly ordem: readonly string[];
  // As linhas marcadas, para a tela apagá-las (`.arrastando`, opacidade 0,4). É um conjunto porque no
  // legado a classe é por linha: o toque que não termina deixa a linha apagada, e um segundo toque
  // em outra linha apaga as duas.
  readonly marcadas: ReadonlySet<string>;
  // A referência do grupo: o toque só troca linhas que moram dentro dele (`container.contains`).
  readonly refDoGrupo: (elemento: HTMLElement | null) => void;
  // Os atributos e ouvintes de uma linha.
  readonly propsDaLinha: (chave: string) => {
    readonly draggable: true;
    readonly [ATRIBUTO]: string;
    readonly onDragStart: () => void;
    readonly onDragEnd: () => void;
    readonly onDragOver: (evento: DragEvent<HTMLElement>) => void;
    readonly onPointerDown: (evento: EventoDePonteiro<HTMLElement>) => void;
    readonly onPointerMove: (evento: EventoDePonteiro<HTMLElement>) => void;
    readonly onPointerUp: (evento: EventoDePonteiro<HTMLElement>) => void;
  };
}

/**
 * O gancho: recebe as chaves na ordem dos dados e a gravação, e devolve a ordem ao vivo e os
 * ouvintes das linhas. `aoTerminar` recebe a ordem final, como o `aoTerminar` do legado.
 */
export function useArrasteDeLinhas(chaves: readonly string[], aoTerminar: (ordem: readonly string[]) => void): ArrasteDeLinhas {
  // A assinatura dos dados: muda quando a ordem ou o conjunto dos dados muda, e só então.
  const assinatura = chaves.join('|');
  const [ordem, setOrdem] = useState<readonly string[]>(chaves);
  // A ordem mais recente, lida no fim do gesto sem esperar o React desenhar.
  const ordemAtual = useRef<readonly string[]>(chaves);
  // A linha arrastada: o `linhaArrastada` do legado, UM para o grupo, lido pelo `dragover` do mouse.
  const arrastadaRef = useRef<string | null>(null);
  // As linhas apagadas, uma a uma, como a classe `.arrastando` de cada linha do legado.
  const [marcadas, setMarcadas] = useState<ReadonlySet<string>>(() => new Set());
  // O toque em curso POR LINHA: o `arrastandoTouch` do legado mora no closure de cada linha, e é por
  // isso que, solta a captura, o dedo sobre outra linha não move nem grava nada.
  const toqueAtivo = useRef<Set<string>>(new Set());
  // O grupo, para o `container.contains(alvo)` do toque.
  const grupo = useRef<HTMLElement | null>(null);
  // A gravação mais recente, lida no fim do gesto sem recriar os ouvintes.
  const terminar = useRef(aoTerminar);
  terminar.current = aoTerminar;

  // Os dados mudaram: a ordem volta à deles, como o legado redesenha a lista. O redesenho do legado
  // cria linhas novas, então as marcas e os toques pendentes somem junto.
  useEffect(() => {
    const nova = assinatura === '' ? [] : assinatura.split('|');
    ordemAtual.current = nova;
    setOrdem(nova);
    toqueAtivo.current = new Set();
    arrastadaRef.current = null;
    setMarcadas(new Set());
  }, [assinatura]);

  // Troca a ordem ao vivo, no estado e na referência.
  const reordenar = useCallback((chave: string, alvo: string, depois: boolean) => {
    const nova = mover(ordemAtual.current, chave, alvo, depois);
    ordemAtual.current = nova;
    setOrdem(nova);
  }, []);

  // Começa o gesto: a linha vira a arrastada e fica apagada (`linhaArrastada = linha` e `classList.add`).
  const comecar = useCallback((chave: string) => {
    arrastadaRef.current = chave;
    setMarcadas((atuais) => new Set(atuais).add(chave));
  }, []);

  // Termina o gesto DESTA linha: só ela volta ao normal, e a arrastada do grupo esvazia.
  const acabar = useCallback((chave: string) => {
    arrastadaRef.current = null;
    setMarcadas((atuais) => {
      const novas = new Set(atuais);
      novas.delete(chave);
      return novas;
    });
  }, []);

  const propsDaLinha = useCallback(
    (chave: string) => ({
      draggable: true as const,
      [ATRIBUTO]: chave,
      // MOUSE: o `dragstart` marca a linha (`admin.html:9109`).
      onDragStart: () => comecar(chave),
      // MOUSE: o `dragend` desmarca esta linha e grava, sempre (`admin.html:9113`).
      onDragEnd: () => {
        acabar(chave);
        terminar.current(ordemAtual.current);
      },
      // MOUSE: passar por cima de outra linha do grupo a move ao vivo (`admin.html:9118`).
      onDragOver: (evento: DragEvent<HTMLElement>) => {
        // Sem o `preventDefault`, o navegador não aceita soltar aqui.
        evento.preventDefault();
        const emCurso = arrastadaRef.current;
        if (!emCurso || emCurso === chave) return;
        reordenar(emCurso, chave, passouDaMetade(evento.currentTarget, evento.clientY));
      },
      // TOQUE: começa fora de botão e de link, e captura o dedo (`admin.html:9128`).
      onPointerDown: (evento: EventoDePonteiro<HTMLElement>) => {
        if (evento.pointerType !== 'touch') return;
        if ((evento.target as Element).closest('button, a')) return;
        toqueAtivo.current.add(chave);
        comecar(chave);
        evento.currentTarget.setPointerCapture(evento.pointerId);
      },
      // TOQUE: acha a linha embaixo do dedo, só do mesmo grupo, e move ao vivo (`admin.html:9136`).
      // Só responde a linha cujo toque está em curso, e não qualquer linha do grupo.
      onPointerMove: (evento: EventoDePonteiro<HTMLElement>) => {
        if (evento.pointerType !== 'touch' || !toqueAtivo.current.has(chave)) return;
        // Impede o navegador de rolar a página no meio do arraste.
        evento.preventDefault();
        const embaixo = document.elementFromPoint(evento.clientX, evento.clientY);
        const alvo = embaixo?.closest(`[${ATRIBUTO}]`);
        if (!alvo || alvo === evento.currentTarget || !grupo.current?.contains(alvo)) return;
        const chaveDoAlvo = alvo.getAttribute(ATRIBUTO);
        if (!chaveDoAlvo) return;
        reordenar(chave, chaveDoAlvo, passouDaMetade(alvo, evento.clientY));
        // O defeito do legado, no seu ponto único: o `insertBefore` dele solta a captura SEMPRE que
        // acha uma linha, mude ela de lugar ou não (achado no cabeçalho deste arquivo).
        if (REPRODUZIR_PERDA_DE_CAPTURA_NO_TOQUE && evento.currentTarget.hasPointerCapture(evento.pointerId)) {
          evento.currentTarget.releasePointerCapture(evento.pointerId);
        }
      },
      // TOQUE: solta, desmarca e grava, só na linha cujo toque está em curso (`admin.html:9146`).
      onPointerUp: (evento: EventoDePonteiro<HTMLElement>) => {
        if (evento.pointerType !== 'touch' || !toqueAtivo.current.has(chave)) return;
        toqueAtivo.current.delete(chave);
        acabar(chave);
        terminar.current(ordemAtual.current);
      },
    }),
    [comecar, acabar, reordenar],
  );

  // O grupo guardado pela referência de função, que o React chama ao montar e ao desmontar.
  const refDoGrupo = useCallback((elemento: HTMLElement | null) => {
    grupo.current = elemento;
  }, []);

  return { ordem, marcadas, refDoGrupo, propsDaLinha };
}
